import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Alert,
  FormControl,
  Snackbar,
  TextField,
  TextFieldProps,
  Theme,
  useTheme,
} from "@mui/material";
import {
  ChangeEvent,
  Dispatch,
  FC,
  FormEventHandler,
  ReactNode,
  SetStateAction,
  useMemo,
  useRef,
} from "react";

import Color from "~/lib/client/design";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";

type Props = {
  children: ReactNode;
  title: ReactNode;
  secondaryText?: ReactNode;
  onSubmit: FormEventHandler<HTMLFormElement>;
  error: {
    errorMessage: string;
    setErrorMessage: Dispatch<SetStateAction<string>>;
  };
  maxWidth?: number;
};

type AccountFormTextFieldProps = {
  id: string;
  label: string;
  type: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
};

export const AccountFormTextField: FC<
  AccountFormTextFieldProps & TextFieldProps
> = ({ id, label, type, onChange, required, ...rest }) => {
  return (
    <StyleWrappedFormControl variant="standard" fullWidth>
      <StyleWrappedTextField
        variant="outlined"
        fullWidth
        id={id}
        label={label}
        type={type}
        required={required}
        onChange={onChange}
        {...rest}
      />
    </StyleWrappedFormControl>
  );
};

export const AccountForm: FC<Props> = ({
  onSubmit,
  title,
  secondaryText,
  children,
  maxWidth = 360,
  error: { errorMessage, setErrorMessage },
}) => {
  const isSpMode = useIsSpMode();
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  const snackbarStyle = useMemo(() => {
    const verticalOffset = isSpMode
      ? (ref.current?.clientHeight ?? 0) + 12
      : (ref.current?.clientHeight ?? 0);

    const transformX = isSpMode ? "0" : "-50%";
    const width = isSpMode ? "auto" : "100%";

    return css`
      &.MuiSnackbar-root {
        transform: translate(${transformX}, ${verticalOffset}px);
        width: ${width};
      }
    `;
  }, [isSpMode]);

  return (
    <div css={rootStyle(theme, maxWidth)} ref={ref}>
      {title}
      {secondaryText}

      <form onSubmit={onSubmit} css={formStyle}>
        {children}
      </form>

      <Snackbar
        // vertical: "top" にしないと行数が増えた場合にレイアウトが崩れるので対応
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!errorMessage}
        autoHideDuration={2000}
        onClose={() => {
          setErrorMessage("");
        }}
        css={snackbarStyle}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

const rootStyle = (theme: Theme, maxWidth: number) => css`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  background-color: ${theme.palette.background.paper};
  filter: drop-shadow(1px 3px 5px rgba(0, 0, 0, 0.2));
  border-radius: 4px;
  width: calc(100% - 2rem);
  max-width: ${maxWidth}px;
`;

export const accountFormLinkStyle = css`
  color: ${Color.primary};
  margin-inline: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const formStyle = css`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const StyleWrappedFormControl = styled(FormControl)({
  "& .MuiFormControl-root": {
    marginBlock: "0.5rem",
  },
});

const StyleWrappedTextField = styled(TextField)({
  "&": {
    color: "#282828",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
  },
  "& label.Mui-focused": {
    color: "#101010",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#B2BAC2",
  },
  "& .MuiOutlinedInput-root": {
    color: "#282828",

    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: "#707070",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
    },
  },
});
